import type { Notice } from "../services/api/types";

export const MANUAL_REMINDER_TITLE_PREFIX = "Reminder - ";

export const MANUAL_REMINDER_BODY_INTRO =
  "Please note this is a reminder concerning the notice below.";

export function buildManualReminderTitle(title: string): string {
  const trimmed = (title || "").trim();
  if (!trimmed) return MANUAL_REMINDER_TITLE_PREFIX.trim();
  if (/^reminder\s*[-–—]/i.test(trimmed)) {
    return trimmed;
  }
  return `${MANUAL_REMINDER_TITLE_PREFIX}${trimmed}`;
}

export function buildManualReminderBody(originalBody: string): string {
  const body = (originalBody || "").trim();
  return body
    ? `${MANUAL_REMINDER_BODY_INTRO}\n\n${body}`
    : MANUAL_REMINDER_BODY_INTRO;
}

export type ManualReminderOptions = {
  /** When set, reminder goes to this parent only (not the full poll audience). */
  targetUserId?: string;
};

/** Payload for POST /v1/notices — same audience as the source, reminder copy. */
export function buildManualReminderNoticePayload(
  notice: Notice,
  options: ManualReminderOptions = {}
): Record<string, unknown> {
  const singleUserId = options.targetUserId?.trim();
  const uploadedFiles =
    notice.attachments?.map((att) => ({
      fileUrl: att.url,
      fileName: att.name,
      contentType: att.type,
      size: att.size ?? 0,
    })) ?? undefined;

  const isEvent =
    notice.type === "event" || Boolean(notice.eventStartDate?.trim());

  return {
    title: buildManualReminderTitle(notice.title),
    body: buildManualReminderBody(notice.body),
    type: notice.type || "info",
    isUrgent: notice.isUrgent === true,
    // API rejects targetUserId + targetSubcircle together — single-user reminders
    // must omit subcircle even when the source poll was circle-scoped.
    targetSubcircle: singleUserId
      ? undefined
      : notice.targetSubcircle || undefined,
    targetUserId: singleUserId || notice.targetUserId || undefined,
    uploadedFiles:
      uploadedFiles && uploadedFiles.length > 0 ? uploadedFiles : undefined,
    eventStartDate: notice.eventStartDate || undefined,
    eventStartTime: notice.eventStartTime || undefined,
    eventEndDate: notice.eventEndDate || undefined,
    eventEndTime: notice.eventEndTime || undefined,
    eventLocation: notice.eventLocation || undefined,
    eventBackgroundImage: notice.eventBackgroundImage || undefined,
    eventFontColor: notice.eventFontColor || undefined,
    ...(isEvent
      ? {
          eventReminderMode: "off",
          eventReminderModes: [],
        }
      : {}),
    ...(notice.type === "poll" && notice.pollOptions?.length
      ? {
          pollOptions: notice.pollOptions,
          pollClosesAt: notice.pollClosesAt || undefined,
          ...(notice.pollMode === "claim" ? { pollMode: "claim" as const } : {}),
        }
      : {}),
    ...(notice.rsvpEnabled
      ? {
          rsvpEnabled: true,
          rsvpClosesAt: notice.rsvpClosesAt || undefined,
        }
      : {}),
  };
}
